import _merge from 'lodash/merge';
import _cloneDeep from 'lodash/cloneDeep';
import i18next from 'i18next';
import { i18nLangOfGame } from 'locale/i18n';
import { lotteryInstantTypes, lotteryParticipantTypes } from 'config/promoLotteryTypes';
import { system100, system900, fever500, warning, black, white } from 'Mui/styles/color';

import { DISPLAY_IMAGE_TEXT } from 'editor/game_pickThePrize/components/SettingPreviews/defaultGameSetting';
import { bgTypes } from 'styles/generateBgStyleByType';
import { froalaKeyVisionTypeMapping } from 'components/KeyVision/config';
import { IS_EDITOR_MODE } from 'config/envVariable';

export const getLotteryDefaultAnimationSettingValue = lotteryType => {
    const t = id => (IS_EDITOR_MODE ? i18nLangOfGame.t(id) : i18next.t(id));
    const config = {
        [lotteryInstantTypes.JOIN_AND_GET]: {},
        [lotteryParticipantTypes.SCRATCH_CARD]: {},
        [lotteryParticipantTypes.WIN_WHEEL]: {},
        [lotteryParticipantTypes.POKE_GAME]: {},
        [lotteryParticipantTypes.RED_ENVELOPE_RAIN]: {},
        [lotteryParticipantTypes.SLOTS]: {},
        [lotteryParticipantTypes.PICK_THE_PRIZE]: {
            /**
             * 點數兌獎設計 - 主視覺
             * isShowKeyVision: 是否顯示主視覺
             * defaultMainVisionType: 背景類型 ( 1 : 自訂背景 / 2 : 預設背景 )
             * bgType: 主視覺背景類型 ( 1 : 顏色 / 2 : 圖片 / 3 : 文字＋背景  )
             * bgColor: 主視覺背景(顏色 / 文字＋背景) 之 預設背景顏色
             * customImgUrl: 自訂背景圖片 url (電腦)
             * customMobileImgUrl 自訂背景圖片 url (手機)
             * froalaEditor: 主視覺背景(文字＋背景) 之 Froala 編輯器內容
             **/
            keyVisionSettings: {
                isShowKeyVision: true,
                defaultMainVisionType: froalaKeyVisionTypeMapping.COLOR,
                bgType: bgTypes.FROALA,
                bgColor: 'rgba(0,67,216,1)',
                customImgUrl: '',
                customMobileImgUrl: '',
                froalaEditor: `<h2 class="section-title" style="text-align: center;"> <span style="color:#FFF;"> <strong class="bold"> ${t(
                    'label.game_type_pick_the_prize'
                )} </strong> </span> </h2>`
            },
            /**
             * 好禮自由選設計 - 獎品樣式
             * type: "image"(純圖片), "text"(純文字), "both"(圖片 & 文字)
             * isShowCaption: boolean 顯示獎品說明
             * textColor: string 文字顏色
             * bgColor: string 背景顏色
             **/
            prizeCardStyleSettings: {
                type: DISPLAY_IMAGE_TEXT,
                isShowCaption: true,
                textColor: black.color,
                bgColor: white.color,
                hintColor: black.color
            },

            /**
             * 好禮自由選設計 - 獎品順序
             * prizesOrder: Prize[]
             **/
            prizeCardOrderSettings: {
                prizesOrder: []
            },
            /**
             * 好禮自由選設計 - 排列方式
             * desktop: x1, x2, x3, x4
             * mobile: x1, x2
             **/
            prizeCardLayoutSettings: {
                desktop: 4,
                mobile: 2
            },
            /**
             * 好禮自由選設計 - 領取取品按鈕
             * color: string 按鈕文字顏色
             * bgColor: string 按鈕背景顏色
             * defaultBackgroundType: 自訂樣式  ( 1 : 自訂背景 / 2 : 預設背景 )
             * customImgUrl: string 按鈕圖片
             **/
            actionButtonSettings: {
                color: white.color,
                bgColor: black.color,
                defaultBackgroundType: froalaKeyVisionTypeMapping.COLOR,
                customImgUrl: ''
            },
            /**
             * 點數兌獎設計/好禮自由選設計 - 背景
             * bgColor: 背景顏色
             * defaultBackgroundType: 自訂樣式  ( 1 : 自訂背景 / 2 : 預設背景 )
             * customImgUrl: 背景圖片
             **/
            backgroundSettings: {
                bgColor: system100.color,
                defaultBackgroundType: froalaKeyVisionTypeMapping.COLOR,
                customImgUrl: ''
            }
        },
        [lotteryParticipantTypes.POINTS_REDEEM]: {
            /**
             * 點數兌獎設計 - 主視覺
             * isShowKeyVision: 是否顯示主視覺
             * defaultMainVisionType: 背景類型 ( 1 : 自訂背景 / 2 : 預設背景 )
             * bgType: 主視覺背景類型 ( 1 : 顏色 / 2 : 圖片 / 3 : 文字＋背景  )
             * bgColor: 主視覺背景(顏色 / 文字＋背景) 之 預設背景顏色
             * customImgUrl: 自訂背景圖片 url (電腦)
             * customMobileImgUrl 自訂背景圖片 url (手機)
             * froalaEditor: 主視覺背景(文字＋背景) 之 Froala 編輯器內容
             **/
            keyVisionSettings: {
                isShowKeyVision: true,
                defaultMainVisionType: froalaKeyVisionTypeMapping.COLOR,
                bgType: bgTypes.FROALA,
                bgColor: warning.color,
                customImgUrl: '',
                customMobileImgUrl: '',
                froalaEditor: `<h2 class="section-title" style="text-align: center;"> <span style="color:#FFF;"> <strong class="bold"> ${t(
                    'label.game_type_points_redeem'
                )} </strong> </span> </h2>`
            },
            /**
             * 點數兌獎設計 - 獎品樣式
             * textColor: 文字顏色
             * bgColor: 背景顏色
             * buttonTextColor: 按鈕文字顏色
             * buttonBgColor: 按鈕背景顏色
             **/
            prizeCardStyleSettings: {
                textColor: system900.color,
                bgColor: warning.color,
                buttonTextColor: white.color,
                buttonBgColor: fever500.color
            },
            /**
             * 點數兌獎設計/好禮自由選設計 - 獎品順序
             * prizesOrder: Prize[]
             **/
            prizeCardOrderSettings: {
                prizesOrder: []
            },
            /**
             * 點數兌獎設計/好禮自由選設計 - 排列方式
             * desktop: x1, x2, x3, x4
             * mobile: x1, x2
             **/
            prizeCardLayoutSettings: {
                desktop: 4,
                mobile: 2
            },
            /**
             * 點數兌獎設計/好禮自由選設計 - 背景
             * bgColor: 背景顏色
             * defaultBackgroundType: 自訂樣式  ( 1 : 自訂背景 / 2 : 預設背景 )
             * customImgUrl: 背景圖片
             **/
            backgroundSettings: {
                bgColor: system100.color,
                defaultBackgroundType: froalaKeyVisionTypeMapping.COLOR,
                customImgUrl: ''
            }
        }
    };

    const defaults = {};
    return _merge(_cloneDeep(defaults), config[lotteryType] || {});
};
